import { inject, Injectable, makeStateKey, TransferState } from '@angular/core';
import type { Observable } from 'rxjs';
import { ReplaySubject, Subject } from 'rxjs';

import { L10N_CONFIG } from 'src/providers/l10n/l10n.module';
import type { ILocale } from 'src/providers/l10n/types';
import { LocaleService } from 'src/services/locale/locale.service';
import { resolveDuplicateSlashUrl } from 'src/shared/helpers/resolve-duplicate-slash-url/resolve-duplicate-slash-url';

import type { ILocaleList, ILocaleWithLink, ILocaliseListResponse } from './entities';
import { LocalisedLinkLevel } from './entities';
import { LocaliseServiceErrorModel } from './localise-service-error.model';

@Injectable({ providedIn: 'root' })
export class UrlLocaliseService {
    private l10nConfig = inject(L10N_CONFIG);
    private transferState = inject(TransferState);
    private localeService = inject(LocaleService);

    private readonly currentLocalisedLinksSubject$ = new ReplaySubject<ILocaleWithLink[]>(1);
    private readonly errorsSubject$ = new Subject<LocaliseServiceErrorModel>();
    private currentLocalisedLinks: ILocaleWithLink[] = [];

    public get currentLocalisedLinks$(): Observable<ILocaleWithLink[]> {
        return this.currentLocalisedLinksSubject$.asObservable();
    }

    public getCurrentLocalisedLinks(): ILocaleWithLink[] {
        return this.currentLocalisedLinks;
    }

    public get errors$(): Observable<LocaliseServiceErrorModel> {
        return this.errorsSubject$.asObservable();
    }

    public generateLocalisedLinks(
        currentPageUrl: string,
        projectHomeUrl: string,
        mainHomeUrl: string,
        localizedUrls?: ILocaliseListResponse
    ): ILocaleWithLink[] {
        if (!localizedUrls) localizedUrls = {};

        const { currentPageLocales, projectHomeLocales } = this.resolveLocaliseLists(
            resolveDuplicateSlashUrl(currentPageUrl),
            resolveDuplicateSlashUrl(projectHomeUrl),
            mainHomeUrl,
            localizedUrls
        );

        const localesWithLink = this.l10nConfig.locales.reduce(
            (acc: ILocaleWithLink[], locale: ILocale): ILocaleWithLink[] => {
                let localeLevel: LocalisedLinkLevel;
                let localeUrl: string;

                if (Object.keys(currentPageLocales).includes(locale.slug)) {
                    localeLevel = LocalisedLinkLevel.CurrentPage;
                    localeUrl = currentPageLocales[locale.slug];
                } else if (Object.keys(projectHomeLocales).includes(locale.slug)) {
                    localeLevel = LocalisedLinkLevel.ProjectHome;
                    localeUrl = projectHomeLocales[locale.slug];
                } else {
                    localeLevel = LocalisedLinkLevel.MainHome;
                    localeUrl = resolveDuplicateSlashUrl(
                        mainHomeUrl +
                            '/' +
                            (locale.slug === this.localeService.getDefaultLocaleSlug() ? '' : locale.slug + '/')
                    );
                }

                acc.push({
                    ...locale,
                    level: localeLevel,
                    url: localeUrl,
                });

                return acc;
            },
            []
        );

        this.currentLocalisedLinks = localesWithLink;
        this.currentLocalisedLinksSubject$.next(localesWithLink);

        return localesWithLink;
    }

    private resolveLocaliseLists(
        currentPageUrl: string,
        projectHomeUrl: string,
        mainHomeUrl: string,
        localizedUrls: ILocaliseListResponse
    ): { currentPageLocales: ILocaleList; projectHomeLocales: ILocaleList } {
        const localeListsFromTransferState = {};
        const urlsList = this.addCurrentToRelativeUrlsList(localizedUrls, mainHomeUrl);

        if (localeListsFromTransferState[currentPageUrl] && localeListsFromTransferState[projectHomeUrl]) {
            return {
                currentPageLocales: localeListsFromTransferState[currentPageUrl],
                projectHomeLocales: localeListsFromTransferState[projectHomeUrl],
            };
        }

        return {
            currentPageLocales:
                localeListsFromTransferState[currentPageUrl] || this.setLocaliseListToState(currentPageUrl, urlsList),
            projectHomeLocales:
                localeListsFromTransferState[projectHomeUrl] || this.setLocaliseListToState(projectHomeUrl, urlsList),
        };
    }

    private addCurrentToRelativeUrlsList(localizedUrls: ILocaliseListResponse, origin: string): ILocaliseListResponse {
        const list = {};

        for (const primaryRelativeUrl in localizedUrls) {
            list[resolveDuplicateSlashUrl(origin + primaryRelativeUrl)] = this.addCurrentDomainToLocaliseUrlsList(
                localizedUrls[primaryRelativeUrl],
                origin
            );
        }

        return list;
    }

    private addCurrentDomainToLocaliseUrlsList(localiseList: ILocaleList, origin: string): ILocaleList {
        const updatedList = {};

        Object.keys(localiseList).map((locale) => {
            updatedList[locale] = resolveDuplicateSlashUrl(origin + localiseList[locale]);
        });

        return updatedList;
    }

    private setLocaliseListToState(url: string, list: ILocaliseListResponse): ILocaleList {
        if (!list[url]) {
            this.emitServiceError(
                new LocaliseServiceErrorModel('page-not-exists', `${url} is not exists in Localise List`)
            );
            return {};
        } else {
            this.transferState.set(makeStateKey(url), list[url]);
            return list[url];
        }
    }

    private emitServiceError(serviceError: LocaliseServiceErrorModel): void {
        this.errorsSubject$.next(serviceError);
    }
}
